import { z } from "zod";

// Evidence is a grounding constraint, not a display feature: forcing every
// claim to cite segment IDs + a short verbatim quote suppresses fabrication.
// The rendered notes do not show it, so quotes stay capped — they are paid
// for in output tokens.
const CitationSchema = z.object({
  segment_ids: z.array(z.string()).describe("IDs of transcript segments that support this claim"),
  quote: z.string().describe("Short verbatim quote from the transcript, 12 words or fewer"),
  confidence: z.enum(["low", "medium", "high"]).describe("Confidence level of the citation"),
});

const ActionItemSchema = z.object({
  owner: z.string().describe("Person responsible for this action"),
  task: z.string().describe("Description of the task"),
  due_date: z.string().optional().nullable().describe("Due date if mentioned"),
  priority: z.enum(["high", "medium", "low"]).describe("Priority level"),
  evidence: z.array(CitationSchema).describe("Supporting evidence from transcript"),
});

const AgendaItemSchema = z.object({
  topic: z.string().describe("Main topic discussed"),
  summary: z.string().describe("Summary of the discussion"),
  decisions: z.array(z.string()).describe("Key decisions made"),
  action_items: z.array(ActionItemSchema).describe("Action items from this topic"),
});

const OpenQuestionSchema = z.object({
  question: z.string().describe("The open question"),
  context: z.string().describe("Context around why this is still open"),
  evidence: z.array(CitationSchema).describe("Supporting evidence"),
});

const RiskSchema = z.object({
  risk: z.string().describe("Description of the risk"),
  severity: z.enum(["critical", "high", "medium", "low"]).describe("Risk severity"),
  mitigation: z.string().optional().nullable().describe("Proposed mitigation if discussed"),
  evidence: z.array(CitationSchema).describe("Supporting evidence"),
});

// Strict structured outputs (`zodTextFormat`, strict: true) reject schemas
// containing a `default` keyword with a 400 — never add `.default()` here.
// The mock server can't catch that (it doesn't validate strictness); the
// real API does. Optional fields must stay `.optional().nullable()`.
export const MeetingMinutesSchema = z.object({
  title: z.string().describe("Title of the meeting"),
  date: z.string().describe("Date of the meeting"),
  attendees: z.array(z.string()).optional().nullable().describe("List of attendees if mentioned"),
  agenda_items: z.array(AgendaItemSchema).describe("Main agenda items discussed"),
  action_items: z.array(ActionItemSchema).describe("All action items from the meeting"),
  open_questions: z.array(OpenQuestionSchema).describe("Questions that remain unresolved"),
  risks: z.array(RiskSchema).optional().nullable().describe("Identified risks or concerns"),
});

export type MeetingMinutes = z.infer<typeof MeetingMinutesSchema>;

